/**
 * Hook to get effective site configuration
 * 
 * This hook merges database values (from CMS) with default config values,
 * ensuring database values take precedence when available.
 */

import { useMemo } from "react";
import { siteConfig, type SiteConfig } from "./site-config";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import type { OrganizationDetails } from "@/services/siteSettingsService";

/**
 * Merge organization details from database with defaults
 */
const mergeOrganizationDetails = (
  dbDetails: OrganizationDetails | null | undefined,
  defaults: SiteConfig
): SiteConfig => {
  if (!dbDetails) {
    return defaults;
  }

  return {
    ...defaults,
    organizationName: dbDetails.organizationName ?? defaults.organizationName,
    tagline: dbDetails.tagline ?? defaults.tagline,
    description: dbDetails.description ?? defaults.description,
    foundedYear: dbDetails.foundedYear ?? defaults.foundedYear,
    logoUrl: (dbDetails.logoUrl && dbDetails.logoUrl.trim() !== '') ? dbDetails.logoUrl : defaults.logoUrl,
    contact: {
      address: {
        street: dbDetails.contact?.address?.street ?? defaults.contact.address.street,
        city: dbDetails.contact?.address?.city ?? defaults.contact.address.city,
        state: dbDetails.contact?.address?.state ?? defaults.contact.address.state,
        zip: dbDetails.contact?.address?.zip ?? defaults.contact.address.zip,
        country: dbDetails.contact?.address?.country ?? defaults.contact.address.country,
      },
      phone: dbDetails.contact?.phone ?? defaults.contact.phone,
      email: dbDetails.contact?.email ?? defaults.contact.email,
      hoursOfOperation: dbDetails.contact?.hoursOfOperation ?? defaults.contact.hoursOfOperation,
    },
    socialLinks: dbDetails.socialLinks ?? defaults.socialLinks,
    hero: {
      title: dbDetails.hero?.title ?? defaults.hero.title,
      highlightedText: dbDetails.hero?.highlightedText ?? defaults.hero.highlightedText,
      subtitle: dbDetails.hero?.subtitle ?? defaults.hero.subtitle,
      showStats: dbDetails.hero?.showStats ?? defaults.hero.showStats,
      stats: dbDetails.hero?.stats ?? defaults.hero.stats,
    },
    legacyCTA: {
      title: dbDetails.legacyCTA?.title ?? defaults.legacyCTA.title,
      description: dbDetails.legacyCTA?.description ?? defaults.legacyCTA.description,
    },
    welcomeSection: {
      title: dbDetails.welcomeSection?.title ?? defaults.welcomeSection.title,
      highlightedText: dbDetails.welcomeSection?.highlightedText ?? defaults.welcomeSection.highlightedText,
      description: dbDetails.welcomeSection?.description ?? defaults.welcomeSection.description,
      buttonText: dbDetails.welcomeSection?.buttonText ?? defaults.welcomeSection.buttonText,
      buttonLink: dbDetails.welcomeSection?.buttonLink ?? defaults.welcomeSection.buttonLink,
      enabled: dbDetails.welcomeSection?.enabled ?? defaults.welcomeSection.enabled,
    },
    seo: {
      defaultTitle: dbDetails.seo?.defaultTitle ?? defaults.seo.defaultTitle,
      titleTemplate: dbDetails.seo?.titleTemplate ?? defaults.seo.titleTemplate,
      defaultDescription: dbDetails.seo?.defaultDescription ?? defaults.seo.defaultDescription,
      defaultKeywords: dbDetails.seo?.defaultKeywords ?? defaults.seo.defaultKeywords,
    },
  };
};

/**
 * Hook to get effective site configuration
 * 
 * Returns siteConfig merged with database values (if available).
 * Database values take precedence over defaults.
 */
export const useSiteConfig = (): SiteConfig => {
  const { organizationDetails } = useSiteSettings();

  return useMemo(() => {
    return mergeOrganizationDetails(organizationDetails, siteConfig);
  }, [organizationDetails]);
};


